'use client';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Link } from '@/navigation';
import { Entry } from 'contentful';
import classNames from 'classnames';
import {
  TypeListFields,
  TypeImage,
  TypeLaunchDarklyVariationContainerFields,
  TypeLink_externalFields,
} from '@/core/generated-types';
import useAuth from '@/context/AuthContext';
import * as Contentful from 'contentful';
import Cart from '@/components/ui/Cart/Cart';
import { Image } from '@/components/ui/Image';
import HamburgerMenuLinks from './HamburgerMenuLinks';
import { UniversalLinkFields } from '../UniversalLink';
import HamburgerIcon from './HamburgerIcon';
import { motion } from 'framer-motion';
import { UniversalCta } from '../UniversalCta';
import { useNavbarAnimations } from '@/components/homepage-us/NavMenu/utils';

export type HamburgerMenuItem = Entry<
  | UniversalLinkFields
  | TypeListFields
  | TypeLaunchDarklyVariationContainerFields
>;

export interface HamburgerMenuProps {
  menu: HamburgerMenuItem[];
  userMenuLoggedOut: HamburgerMenuItem[] | undefined;
  userMenuLoggedIn: HamburgerMenuItem[] | undefined;
  logoutText: string;
  cartText: string;
  logo?: TypeImage;
  playKitsByAgeLinks?: Contentful.Entry<Record<string, any>>[];
  playKitsDropdownLinks?: Contentful.Entry<Record<string, any>>[];
  playKistByAgeCta?: Contentful.Entry<TypeLink_externalFields>;
  shopAllPrimaryLinks?: Contentful.Entry<Record<string, any>>[];
  shopAllAdditionalLinks?: Contentful.Entry<Record<string, any>>[];
}

const HamburgerMenu: FC<HamburgerMenuProps> = ({
  menu,
  userMenuLoggedOut,
  userMenuLoggedIn,
  logoutText,
  cartText,
  logo,
  playKitsByAgeLinks,
  playKitsDropdownLinks,
  playKistByAgeCta,
  shopAllPrimaryLinks,
  shopAllAdditionalLinks,
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [bannerOffset, setBannerOffset] = useState(0);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [clientSideStyles, setClientSideStyles] = useState({});
  const { ctaOpacity, moveLogo, scaleLogo, isCTAHidden } = useNavbarAnimations({
    isDesktop: false,
  });

  const { user } = useAuth();

  const openMenu = useCallback(() => {
    const offset = Math.max(40 - document.documentElement.scrollTop, 0);
    setBannerOffset(offset);
    setShowMobileMenu(true);
    document.body.classList.toggle('overflow-hidden', !showMobileMenu);
  }, [showMobileMenu]);

  const closeMenu = useCallback(() => {
    setShowMobileMenu(false);
    document.body.classList.remove('overflow-hidden');
  }, []);

  const onMenuItemTap = useCallback(() => {
    closeMenu();
  }, [closeMenu]);

  const toggleMobileMenu = useCallback(() => {
    if (showMobileMenu) {
      closeMenu();
    } else {
      openMenu();
    }
  }, [showMobileMenu, openMenu, closeMenu]);

  useEffect(() => {
    const resize = () => {
      if (showMobileMenu) closeMenu();
    };

    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [showMobileMenu, closeMenu]);

  useEffect(() => {
    const onScroll = () => {
      const { scrollTop } = window.document.documentElement;
      if (scrollTop <= 0 && isScrolled) {
        setIsScrolled(false);
      } else if (scrollTop > 0 && !isScrolled) {
        setIsScrolled(true);
      }
    };

    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [isScrolled]);

  useEffect(() => {
    setClientSideStyles({
      height: showMobileMenu ? `calc(100vh - ${bannerOffset}px)` : '',
    });
  }, [showMobileMenu, bannerOffset]);

  return (
    <div
      data-testid="mobile-topnav"
      className={classNames('z-50 lg:hidden', {
        'flex flex-col': showMobileMenu,
      })}
      style={clientSideStyles}
    >
      <nav
        className={classNames(
          'relative z-50 flex h-[72px] flex-none items-center bg-white px-4 md:px-8',
          {
            'shadow-lg': isScrolled,
          },
        )}
      >
        <button
          onClick={toggleMobileMenu}
          aria-label="Nav button"
          data-testid="hamburger-icon"
        >
          <HamburgerIcon />
        </button>
        {logo && (
          <motion.div
            style={{
              left: moveLogo,
            }}
            className="absolute w-[164px] -translate-x-1/2 transform"
          >
            <motion.div style={{ scale: scaleLogo }}>
              <Link href="/" onClick={onMenuItemTap}>
                <Image imageData={logo} width={167} height={18} alt="" />
              </Link>
            </motion.div>
          </motion.div>
        )}
        <div className="absolute right-4 top-1/2 -translate-y-1/2 transform">
          {playKistByAgeCta && !isCTAHidden && (
            <div className="absolute right-0 min-w-[176px] z-20 -top-1">
              <motion.div style={{ opacity: ctaOpacity }}>
                <UniversalCta
                  link={playKistByAgeCta}
                  className="max-h-[50px] px-2 text-sm"
                />
              </motion.div>
            </div>
          )}
          <Cart cartText="" />
        </div>
      </nav>
      {showMobileMenu && (
        <div className="w-full flex-auto overflow-auto bg-blue-deep pb-40 text-white">
          <ul className="px-4 md:px-8">
            <HamburgerMenuLinks
              menu={menu}
              onMenuItemTap={onMenuItemTap}
              playKitsByAgeLinks={playKitsByAgeLinks}
              playKitsDropdownLinks={playKitsDropdownLinks}
              shopAllPrimaryLinks={shopAllPrimaryLinks}
              shopAllAdditionalLinks={shopAllAdditionalLinks}
            />
            {user
              ? userMenuLoggedIn && (
                  <HamburgerMenuLinks
                    menu={userMenuLoggedIn}
                    onMenuItemTap={onMenuItemTap}
                    logoutText={logoutText}
                    playKitsByAgeLinks={playKitsByAgeLinks}
                    playKitsDropdownLinks={playKitsDropdownLinks}
                    shopAllPrimaryLinks={shopAllPrimaryLinks}
                    shopAllAdditionalLinks={shopAllAdditionalLinks}
                  />
                )
              : userMenuLoggedOut && (
                  <HamburgerMenuLinks
                    menu={userMenuLoggedOut}
                    onMenuItemTap={onMenuItemTap}
                    playKitsByAgeLinks={playKitsByAgeLinks}
                    playKitsDropdownLinks={playKitsDropdownLinks}
                    shopAllPrimaryLinks={shopAllPrimaryLinks}
                    shopAllAdditionalLinks={shopAllAdditionalLinks}
                  />
                )}
            <li className="mt-8">
              <Cart
                cartText={cartText}
                classesText="text-h3"
                hoverStyleChange={false}
              />
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default React.memo(HamburgerMenu);
